/**
 * §7.1 使用者、權限與導覽。
 *
 * 關鍵：**導覽由伺服器算好給前端，前端不做權限推導**。
 * 這個 context 只是把 `/api/auth/me` 的結果放好，加上一個 `can()` 讓畫面決定
 * 要不要渲染某個按鈕——那是 §6.4 的前端那一半，**不當作安全機制**。
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, setReauthHandler } from '../api/client.js'
import type { AuthMethods, Me } from '../api/types.js'

interface AuthCtx {
  me: Me | null
  methods: AuthMethods | null
  loading: boolean
  /** 系統尚未初始化（§3.7 第 4 點）：所有 API 回 503 */
  notInitialized: boolean
  /** §6.4 前端這一半：決定要不要渲染。不是安全機制 */
  can: (...permissions: string[]) => boolean
  canAny: (...permissions: string[]) => boolean
  login: (email: string, password: string) => Promise<void>
  loginWithCode: (email: string, code: string) => Promise<void>
  requestCode: (email: string) => Promise<void>
  logout: () => Promise<void>
  switchSite: (siteId: string) => Promise<void>
  reload: () => Promise<void>
  /** §10.4 Session 過期時的重新登入對話框開關 */
  reauthOpen: boolean
  resolveReauth: (ok: boolean) => void
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [methods, setMethods] = useState<AuthMethods | null>(null)
  const [loading, setLoading] = useState(true)
  const [notInitialized, setNotInitialized] = useState(false)
  const [reauthOpen, setReauthOpen] = useState(false)
  const [reauthResolve, setReauthResolve] = useState<((ok: boolean) => void) | null>(null)

  const loadMethods = useCallback(async () => {
    try {
      setMethods(await api.get<AuthMethods>('/api/auth/methods', { noReauth: true }))
      setNotInitialized(false)
    } catch (e) {
      // §3.7：users 表為空且沒設 BOOTSTRAP_ADMIN_EMAIL 時，所有 API 回 503
      if ((e as { code?: string }).code === 'SYSTEM_NOT_INITIALIZED') setNotInitialized(true)
      setMethods(null)
    }
  }, [])

  const reload = useCallback(async () => {
    try {
      setMe(await api.get<Me>('/api/auth/me', { noReauth: true }))
    } catch {
      setMe(null)
      await loadMethods()
    } finally {
      setLoading(false)
    }
  }, [loadMethods])

  useEffect(() => { void reload() }, [reload])

  // §10.4：把 401 交給對話框處理，不離開目前畫面
  useEffect(() => {
    setReauthHandler(() => new Promise<boolean>((resolve) => {
      setReauthOpen(true)
      setReauthResolve(() => resolve)
    }))
    return () => setReauthHandler(null)
  }, [])

  const resolveReauth = useCallback((ok: boolean) => {
    setReauthOpen(false)
    reauthResolve?.(ok)
    setReauthResolve(null)
    if (ok) void reload()
  }, [reauthResolve, reload])

  const login = useCallback(async (email: string, password: string) => {
    await api.post('/api/auth/login', { email, password }, { noReauth: true })
    await reload()
  }, [reload])

  const loginWithCode = useCallback(async (email: string, code: string) => {
    await api.post('/api/auth/code/verify', { email, code }, { noReauth: true })
    await reload()
  }, [reload])

  const requestCode = useCallback(async (email: string) => {
    // §5.3 一律回 200，不透露帳號是否存在
    await api.post('/api/auth/code/request', { email }, { noReauth: true })
  }, [])

  const logout = useCallback(async () => {
    try { await api.post('/api/auth/logout', {}, { noReauth: true }) } catch { /* 已經登出也算成功 */ }
    setMe(null)
    await loadMethods()
  }, [loadMethods])

  const switchSite = useCallback(async (siteId: string) => {
    await api.post('/api/auth/site', { siteId })
    // §12.6 切換即重新載入該廠區的資料與導覽
    await reload()
  }, [reload])

  const permSet = useMemo(() => new Set(me?.permissions ?? []), [me])
  const can = useCallback((...ps: string[]) => ps.every((p) => permSet.has(p)), [permSet])
  const canAny = useCallback((...ps: string[]) => ps.some((p) => permSet.has(p)), [permSet])

  const value: AuthCtx = {
    me, methods, loading, notInitialized, can, canAny,
    login, loginWithCode, requestCode, logout, switchSite, reload,
    reauthOpen, resolveReauth,
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useAuth 必須在 AuthProvider 裡使用')
  return c
}

/**
 * §6.4 前端這一半：依權限決定要不要渲染。
 * **不當作安全機制**——後端每支 API 都會再檢查一次。
 */
export function Can({ perm, any, children, fallback = null }: {
  perm?: string | string[]
  any?: string[]
  children: React.ReactNode
  fallback?: React.ReactNode
}) {
  const { can, canAny } = useAuth()
  const list = perm === undefined ? [] : Array.isArray(perm) ? perm : [perm]
  const ok = any ? canAny(...any) : can(...list)
  return <>{ok ? children : fallback}</>
}
